import UserComp from './User/UserComp';
import GroupCard from './User/GroupCard';
import NoData from '../../components/NoData';
import AppLayout from '../../components/AppLayout';
import NewSecurityKeyModal from './NewSecurityKeyModal';
import ChatUserSkeleton from '../../components/Skeletons/ChatUserSkeleton';
import ChatStyles from './Chat.module.css';
import TabLinks from './User/TabLinks';
import SelectedUser from './GroupChatScreen/Chat/SelectedUser';
import VideoNotesModal from './VideoNotesModal';
import DeleteVideoNoteModal from './DeleteVideoNoteModal';
import AddVideoNoteModal from './AddVideoNoteModal';
import AddGroupUsersMoal from './GroupChatScreen/AddGroupUsersModal/AddGroupUsersMoal';
import GroupInfoModal from './GroupChatScreen/GroupInfoModal';
import SelectedGroup from './GroupChatScreen/Chat/SelectedGroup';
import SuccessToast from '../../components/Toast/SuccessToast';
import ErrorToast from '../../components/Toast/ErrorToast';
import ChatboxPlaceholder from './ChatboxPlaceholder';
import useChatFunctions from './useChatFunctions';
import Header from './Header';
import NoConversations from './NoConversations';
import SelectedChat from './SelectedChat';
import ChatRequestsModal from './ChatRequestsModal';
import { useTranslation } from 'react-i18next';

const ChatScreen = ({ socket }) => {
  const { t } = useTranslation();
  const {
    messagesStartRef,
    messagesEndRef,
    lastTopMessage,
    acceptedUserLoading,
    showAddGroupMoal,
    setShowAddGroupMoal,
    showGroupInfoMoal,
    setShowGroupInfoMoal,
    showChatRequests,
    setShowChatRequests,
    acceptedUsers,
    allUsers,
    selectedUser,
    setSelectedUser,
    showSelectedUser,
    setShowSelectedUser,
    selectedNewUser,
    setSelectedNewUser,
    acceptedUserSearch,
    setAcceptedUserSearch,
    chatUserLoading,
    chatSendLoading,
    showEmojiBox,
    setShowEmojiBox,
    showErrorToast,
    setShowErrorToast,
    showSuccessToast,
    setShowSuccessToast,
    toastMessage,
    showReactBox,
    setShowReactBox,
    removedGroup,
    selectedMessage,
    setSelectedMessage,
    encryptedSecurityKey,
    encryptedChatData,
    setEncryptedChatData,
    decryptedChatData,
    setDecryptedChatData,
    groupedData,
    groupName,
    setGroupName,
    chatText,
    setChatText,
    attachedFiles,
    setAttachedFiles,
    fileInputRef,
    securityKeys,
    securityKeyModal,
    setSecurityKeyModal,
    securityKeyCreate,
    showSecurityKey,
    setShowSecurityKey,
    topMessageRef,
    videoNotesModal,
    setVideoNotesModal,
    group,
    setGroup,
    videoNotesData,
    videoNoteSelected,
    setVideoNoteSelected,
    newVideoNoteText,
    setNewVideoNoteText,
    newVideoNoteType,
    setNewVideoNoteType,
    videoNotesDeleteModal,
    setVideoNotesDeleteModal,
    addNewVideoNoteModal,
    setAddNewVideoNoteModal,
    newVideoNote,
    setNewVideoNote,
    noteType,
    setNoteType,
    userLogin,
    setLoadMoreMsg,
    getAcceptedUsers,
    handleSubmitVideoNote,
    handleEditVideoNote,
    handleDeleteNote,
    decryptMessageByKeyId,
    checkOnline,
    checkOnlineAndGetData,
    newSocketUserMsg,
    getMoreChats,
    chatUser,
    onEmojiClick,
    reactToChat,
    handleCreateKey,
    handleMakeDefaultKey,
    requestVideoCallToSelectedUser,
  } = useChatFunctions(socket);

  // ─── render ────────────────────────────────────────────────────────────────
  return (
    <AppLayout>
      {/* ══ Chat Shell ══════════════════════════════════════════════════ */}
      <div className={ChatStyles.content}>
        {/* ── LEFT PANEL: Conversation list ────────────────────────── */}
        <div className={ChatStyles.chatLists}>
          {/* Header row: Messages title + E2E badge */}
          <Header acceptedUserSearch={acceptedUserSearch} setAcceptedUserSearch={setAcceptedUserSearch} />

          {/* Filter tabs + group chat button */}
          <TabLinks
            link1Label={t('screens.chat.tabAllChats')}
            setShowAddGroupMoal={setShowAddGroupMoal}
            link1Active={true}
            link1Notification=''
            link2Label={t('screens.chat.tabChatRequests')}
            link2Click={() => setShowChatRequests(true)}
            link2Active={false}
            link2Notification={acceptedUsers?.pendingRequestCount > 0 && acceptedUsers.pendingRequestCount}
          />

          {/* Scrollable conversation list */}
          <div className={ChatStyles.chatLists_users1}>
            {acceptedUserLoading ? (
              <ChatUserSkeleton />
            ) : acceptedUsers?.data?.length === 0 ? (
              <NoConversations setShowAddGroupMoal={setShowAddGroupMoal} />
            ) : acceptedUsers?.data?.filter(u => u.name.toLowerCase().includes(acceptedUserSearch.toLowerCase())).length === 0 ? (
              <NoData title={t('screens.chat.noResultsFound')} />
            ) : (
              acceptedUsers?.data
                ?.filter(u => u.name.toLowerCase().includes(acceptedUserSearch.toLowerCase()))
                ?.sort((a, b) => new Date(b?.lastMessage?.messageTime) - new Date(a?.lastMessage?.messageTime))
                ?.map(user =>
                  user.type === 'group' ? (
                    <GroupCard
                      key={user._id}
                      data={{
                        user,
                        setLoadMoreMsg,
                        selectedUser,
                        setShowSelectedUser,
                        setSelectedUser,
                        setSelectedNewUser,
                        setDecryptedChatData,
                        setEncryptedChatData,
                        setShowSecurityKey,
                        decryptMessageByKeyId,
                      }}
                    />
                  ) : (
                    <UserComp
                      key={user._id}
                      data={{
                        user,
                        checkOnline,
                        setLoadMoreMsg,
                        newSocketUserMsg,
                        selectedUser,
                        setShowSelectedUser,
                        setSelectedUser,
                        setSelectedNewUser,
                        setDecryptedChatData,
                        setEncryptedChatData,
                        setShowSecurityKey,
                        decryptMessageByKeyId,
                      }}
                    />
                  ),
                )
            )}
          </div>

          {/* New Secure Message button */}
          <button className={ChatStyles.newMsgBtn} onClick={() => setShowChatRequests(true)}>
            <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
              <line x1='12' y1='5' x2='12' y2='19' />
              <line x1='5' y1='12' x2='19' y2='12' />
            </svg>
            {t('screens.chat.newSecureMessage')}
          </button>
        </div>

        {/* ── RIGHT PANEL: Conversation view ───────────────────────── */}
        <div className={ChatStyles.chat}>
          {selectedUser && showSelectedUser ? (
            <>
              {/* Conversation header */}
              {selectedUser.type === 'group' ? (
                <SelectedGroup
                  data={{
                    chatUserLoading,
                    selectedNewUser,
                    selectedUser,
                    groupName,
                    setGroupName,
                  }}
                  setShowGroupInfoMoal={setShowGroupInfoMoal}
                  requestVideoCallToSelectedUser={requestVideoCallToSelectedUser}
                />
              ) : (
                <SelectedUser
                  data={{
                    chatUserLoading,
                    checkOnline,
                    checkOnlineAndGetData,
                    selectedUser,
                    selectedNewUser,
                    setShowSecurityKey,
                    showSecurityKey,
                    setVideoNotesModal,
                    group,
                    setGroup,
                    socket,
                  }}
                  requestVideoCallToSelectedUser={requestVideoCallToSelectedUser}
                />
              )}

              <SelectedChat
                chatUserLoading={chatUserLoading}
                encryptedSecurityKey={encryptedSecurityKey}
                selectedUser={selectedUser}
                showSecurityKey={showSecurityKey}
                handleMakeDefaultKey={handleMakeDefaultKey}
                setShowSecurityKey={setShowSecurityKey}
                setSecurityKeyModal={setSecurityKeyModal}
                encryptedChatData={encryptedChatData}
                decryptedChatData={decryptedChatData}
                messagesStartRef={messagesStartRef}
                securityKeys={securityKeys}
                getMoreChats={getMoreChats}
                groupedData={groupedData}
                userLogin={userLogin}
                lastTopMessage={lastTopMessage}
                topMessageRef={topMessageRef}
                setSelectedMessage={setSelectedMessage}
                setShowReactBox={setShowReactBox}
                showReactBox={showReactBox}
                reactToChat={reactToChat}
                messagesEndRef={messagesEndRef}
                removedGroup={removedGroup}
                chatText={chatText}
                selectedMessage={selectedMessage}
                fileInputRef={fileInputRef}
                setAttachedFiles={setAttachedFiles}
                setShowEmojiBox={setShowEmojiBox}
                showEmojiBox={showEmojiBox}
                attachedFiles={attachedFiles}
                setChatText={setChatText}
                socket={socket}
                chatUser={chatUser}
                onEmojiClick={onEmojiClick}
                chatSendLoading={chatSendLoading}
              />
            </>
          ) : (
            <ChatboxPlaceholder hasConnections={!!(acceptedUsers?.data?.length > 0)} onNewChat={() => setShowAddGroupMoal(true)} />
          )}
        </div>
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────── */}
      <NewSecurityKeyModal
        securityKeyModal={securityKeyModal}
        loading={securityKeyCreate}
        handleCreateKey={handleCreateKey}
        handleCreateKeyModalClose={() => setSecurityKeyModal(false)}
      />

      {videoNotesModal && (
        <VideoNotesModal
          addNew={true}
          onAddNewClick={() => setAddNewVideoNoteModal({ loading: false, status: true })}
          videoNotesModal={videoNotesModal}
          loading={videoNotesData.loading}
          videoNotes={videoNotesData.data}
          selectedUser={selectedUser}
          handleModalClose={() => setVideoNotesModal(false)}
          data={{
            videoNoteSelected,
            setVideoNoteSelected,
            newVideoNoteText,
            setNewVideoNoteText,
            newVideoNoteType,
            setNewVideoNoteType,
            handleEditVideoNote,
            setVideoNotesDeleteModal,
          }}
        />
      )}

      {addNewVideoNoteModal.status && (
        <AddVideoNoteModal
          loading={addNewVideoNoteModal.loading}
          show={addNewVideoNoteModal.status}
          handleSubmit={handleSubmitVideoNote}
          handleModalClose={() => setAddNewVideoNoteModal({ loading: false, status: false })}
          data={{ newVideoNote, setNewVideoNote, noteType, setNoteType }}
        />
      )}

      {videoNotesDeleteModal.status && (
        <DeleteVideoNoteModal
          loading={videoNotesDeleteModal.loading}
          handleSubmit={handleDeleteNote}
          videoNotesDeleteModal={videoNotesDeleteModal}
          handleModalClose={() => setVideoNotesDeleteModal({ loading: false, status: false, data: {} })}
        />
      )}

      {showAddGroupMoal && (
        <AddGroupUsersMoal
          socket={socket}
          allUsersLoading={acceptedUserLoading}
          acceptedUsers={allUsers}
          getAcceptedUsers={getAcceptedUsers}
          showModal={showAddGroupMoal}
          setShowModal={setShowAddGroupMoal}
        />
      )}

      {showGroupInfoMoal && (
        <GroupInfoModal
          socket={socket}
          groupName={groupName}
          setGroupName={setGroupName}
          setShowSelectedUser={setShowSelectedUser}
          groupId={selectedUser.groupId}
          getAcceptedUsers={getAcceptedUsers}
          acceptedUsers={acceptedUsers}
          showModal={showGroupInfoMoal}
          setShowModal={setShowGroupInfoMoal}
        />
      )}
      {showChatRequests && <ChatRequestsModal socket={socket} onClose={() => setShowChatRequests(false)} />}

      <ErrorToast message={toastMessage} showToast={showErrorToast} onClose={() => setShowErrorToast(false)} duration={3500} />
      <SuccessToast message={toastMessage} showToast={showSuccessToast} onClose={() => setShowSuccessToast(false)} duration={3500} />
    </AppLayout>
  );
};

export default ChatScreen;
